import { blogService } from './blogService';

interface SitemapUrl {
  loc: string;
  lastmod?: string;
  changefreq?: 'always' | 'hourly' | 'daily' | 'weekly' | 'monthly' | 'yearly' | 'never';
  priority?: string;
  images?: Array<{
    loc: string;
    caption?: string;
    title?: string;
  }>;
}

class EnhancedSitemapService {
  private baseUrl = 'https://seniorthrive.com';

  // Static pages with enhanced metadata and SEO priorities
  private staticPages: SitemapUrl[] = [
    // Homepage - Highest Priority
    {
      loc: `${this.baseUrl}/`,
      lastmod: new Date().toISOString().split('T')[0],
      changefreq: 'daily',
      priority: '1.0',
      images: [
        { loc: `${this.baseUrl}/hero-family-home.jpg`, caption: 'SeniorThrive - Empowering families to support older adults at home' }
      ]
    },

    // Core Service Pages - Very High Priority
    {
      loc: `${this.baseUrl}/about-seniorthrive`,
      lastmod: new Date().toISOString().split('T')[0],
      changefreq: 'weekly',
      priority: '0.9'
    },
    {
      loc: `${this.baseUrl}/professional-network`,
      lastmod: new Date().toISOString().split('T')[0],
      changefreq: 'monthly',
      priority: '0.8'
    },
    {
      loc: `${this.baseUrl}/features`,
      lastmod: new Date().toISOString().split('T')[0],
      changefreq: 'weekly',
      priority: '0.8'
    },
    {
      loc: `${this.baseUrl}/pricing`,
      lastmod: new Date().toISOString().split('T')[0],
      changefreq: 'weekly',
      priority: '0.9'
    },

    // Audience-Specific Pages - High Priority
    {
      loc: `${this.baseUrl}/older-adults`,
      lastmod: new Date().toISOString().split('T')[0],
      changefreq: 'monthly',
      priority: '0.8',
      images: [
        { loc: `${this.baseUrl}/senior-confident.jpg`, caption: 'Confident senior using SeniorThrive platform' }
      ]
    },
    {
      loc: `${this.baseUrl}/thrivefamily`,
      lastmod: new Date().toISOString().split('T')[0],
      changefreq: 'monthly',
      priority: '0.8',
      images: [
        { loc: `${this.baseUrl}/family-thrive-circle-main.jpg`, caption: 'Family members connected through SeniorThrive ThriveCircle' }
      ]
    },
    {
      loc: `${this.baseUrl}/family-caregiver-tools`,
      lastmod: new Date().toISOString().split('T')[0],
      changefreq: 'monthly',
      priority: '0.8',
      images: [
        { loc: `${this.baseUrl}/caregiver-support.jpg`, caption: 'Family using SeniorThrive care tools' }
      ]
    },

    // Professional Network Pages
    {
      loc: `${this.baseUrl}/professionals/directory`,
      lastmod: new Date().toISOString().split('T')[0],
      changefreq: 'weekly',
      priority: '0.7'
    },
    {
      loc: `${this.baseUrl}/find`,
      lastmod: new Date().toISOString().split('T')[0],
      changefreq: 'monthly',
      priority: '0.7'
    },
    {
      loc: `${this.baseUrl}/occupational-therapists/directory`,
      lastmod: new Date().toISOString().split('T')[0],
      changefreq: 'weekly',
      priority: '0.6'
    },
    {
      loc: `${this.baseUrl}/caps-specialists/directory`,
      lastmod: new Date().toISOString().split('T')[0],
      changefreq: 'weekly',
      priority: '0.6'
    },
    {
      loc: `${this.baseUrl}/ot-campaign`,
      lastmod: new Date().toISOString().split('T')[0],
      changefreq: 'monthly',
      priority: '0.6'
    },
    {
      loc: `${this.baseUrl}/caregiver-coordination`,
      lastmod: new Date().toISOString().split('T')[0],
      changefreq: 'monthly',
      priority: '0.7'
    },

    // Support & Information Pages
    {
      loc: `${this.baseUrl}/support`,
      lastmod: new Date().toISOString().split('T')[0],
      changefreq: 'weekly',
      priority: '0.7'
    },
    {
      loc: `${this.baseUrl}/support/faq`,
      lastmod: new Date().toISOString().split('T')[0],
      changefreq: 'weekly',
      priority: '0.6'
    },
    {
      loc: `${this.baseUrl}/support/all`,
      lastmod: new Date().toISOString().split('T')[0],
      changefreq: 'weekly',
      priority: '0.5'
    },

    // Content Pages
    {
      loc: `${this.baseUrl}/blog`,
      lastmod: new Date().toISOString().split('T')[0],
      changefreq: 'daily',
      priority: '0.8'
    },
    {
      loc: `${this.baseUrl}/stories`,
      lastmod: new Date().toISOString().split('T')[0],
      changefreq: 'weekly',
      priority: '0.6'
    },

    // Business & Partnership Pages
    {
      loc: `${this.baseUrl}/partners`,
      lastmod: new Date().toISOString().split('T')[0],
      changefreq: 'monthly',
      priority: '0.6'
    },
    {
      loc: `${this.baseUrl}/sponsor-older-adults`,
      lastmod: new Date().toISOString().split('T')[0],
      changefreq: 'monthly',
      priority: '0.5'
    },

    // Contact & Company Pages
    {
      loc: `${this.baseUrl}/contact`,
      lastmod: new Date().toISOString().split('T')[0],
      changefreq: 'monthly',
      priority: '0.7'
    },
    {
      loc: `${this.baseUrl}/about`,
      lastmod: new Date().toISOString().split('T')[0],
      changefreq: 'monthly',
      priority: '0.6'
    },

    // VIP & Early Access Pages
    {
      loc: `${this.baseUrl}/get-vip`,
      lastmod: new Date().toISOString().split('T')[0],
      changefreq: 'weekly',
      priority: '0.8'
    },

    // Legal Pages - Lower Priority
    {
      loc: `${this.baseUrl}/privacy`,
      lastmod: new Date().toISOString().split('T')[0],
      changefreq: 'yearly',
      priority: '0.3'
    },
    {
      loc: `${this.baseUrl}/terms`,
      lastmod: new Date().toISOString().split('T')[0],
      changefreq: 'yearly',
      priority: '0.3'
    },
    {
      loc: `${this.baseUrl}/referral-agreement`,
      lastmod: new Date().toISOString().split('T')[0],
      changefreq: 'yearly',
      priority: '0.3'
    }
  ];

  // Fetch dynamic blog posts
  async fetchBlogPosts(): Promise<SitemapUrl[]> {
    try {
      const { posts } = await blogService.fetchPosts({ per_page: 100, order: 'desc', orderby: 'published_at' });
      const items: SitemapUrl[] = await Promise.all(
        posts.map(async (post) => {
          const featuredUrl = await blogService.getFeaturedImageUrl(post);
          return {
            loc: `${this.baseUrl}/blog/${post.slug}`,
            lastmod: post.modified || post.date || new Date().toISOString().split('T')[0],
            changefreq: 'monthly' as const,
            priority: '0.6',
            images: featuredUrl
              ? [
                  {
                    loc: featuredUrl,
                    caption: post.title?.rendered || '',
                    title: post.title?.rendered || ''
                  }
                ]
              : undefined
          };
        })
      );
      return items;
    } catch (error) {
      console.warn('Failed to fetch blog posts for sitemap:', error);
      return [];
    }
  }

  // Generate complete sitemap
  async generateSitemap(): Promise<string> {
    try {
      const [blogPosts] = await Promise.all([
        this.fetchBlogPosts()
      ]);

      const allUrls = [...this.staticPages, ...blogPosts];
      return this.generateSitemapXml(allUrls);
    } catch (error) {
      console.error('Error generating sitemap:', error);
      // Fallback to static pages only
      return this.generateSitemapXml(this.staticPages);
    }
  }

  // Generate XML from URLs
  private generateSitemapXml(urls: SitemapUrl[]): string {
    const urlEntries = urls.map(url => {
      let entry = `  <url>
    <loc>${this.escapeXml(url.loc)}</loc>`;

      if (url.lastmod) {
        entry += `
    <lastmod>${url.lastmod}</lastmod>`;
      }

      if (url.changefreq) {
        entry += `
    <changefreq>${url.changefreq}</changefreq>`;
      }

      if (url.priority) {
        entry += `
    <priority>${url.priority}</priority>`;
      }

      // Add image information if available
      if (url.images && url.images.length > 0) {
        url.images.forEach(image => {
          entry += `
    <image:image>
      <image:loc>${this.escapeXml(image.loc)}</image:loc>`;
          if (image.caption) {
            entry += `
      <image:caption>${this.escapeXml(image.caption)}</image:caption>`;
          }
          if (image.title) {
            entry += `
      <image:title>${this.escapeXml(image.title)}</image:title>`;
          }
          entry += `
    </image:image>`;
        });
      }

      entry += `
  </url>`;
      return entry;
    }).join('\n');

    return `<?xml version="1.0" encoding="UTF-8"?>
<!-- SeniorThrive Enhanced Sitemap -->
<!-- Generated: ${new Date().toISOString()} -->
<!-- Total URLs: ${urls.length} -->
<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9"
        xmlns:image="http://www.google.com/schemas/sitemap-image/1.1"
        xmlns:news="http://www.google.com/schemas/sitemap-news/0.9">
${urlEntries}
</urlset>`;
  }

  // Escape XML characters
  private escapeXml(unsafe: string): string {
    return unsafe.replace(/[<>&'"]/g, (c) => {
      switch (c) {
        case '<': return '&lt;';
        case '>': return '&gt;';
        case '&': return '&amp;';
        case "'": return '&#39;';
        case '"': return '&quot;';
        default: return c;
      }
    });
  }

  // Generate sitemap index
  generateSitemapIndex(): string {
    const lastmod = new Date().toISOString().split('T')[0];
    
    return `<?xml version="1.0" encoding="UTF-8"?>
<!-- SeniorThrive Sitemap Index -->
<!-- Generated: ${new Date().toISOString()} -->
<sitemapindex xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">
  <sitemap>
    <loc>${this.baseUrl}/sitemap.xml</loc>
    <lastmod>${lastmod}</lastmod>
  </sitemap>
  <sitemap>
    <loc>${this.baseUrl}/blog-sitemap.xml</loc>
    <lastmod>${lastmod}</lastmod>
  </sitemap>
</sitemapindex>`;
  }

  // Save sitemap (placeholder for file writing)
  async saveSitemapToFile(): Promise<void> {
    const sitemapContent = await this.generateSitemap();
    
    console.log('📄 Enhanced Sitemap Generated Successfully!');
    console.log('🔗 Total URLs included:', (sitemapContent.match(/<url>/g) || []).length);
    console.log('📋 Copy the content below to public/sitemap.xml:');
    console.log('='.repeat(50));
    console.log(sitemapContent);
    console.log('='.repeat(50));
    console.log('🚀 Submit to Google: https://search.google.com/search-console/sitemaps');
    console.log('🔍 Validate: https://www.xml-sitemaps.com/validate-xml-sitemap.html');
  }

  // Validate URLs (useful for SEO audits)
  async validateUrls(): Promise<Array<{url: string, status: number, valid: boolean}>> {
    const results = [];
    
    for (const page of this.staticPages.slice(0, 10)) { // Test first 10 to avoid rate limits
      try {
        const response = await fetch(page.loc, { method: 'HEAD' });
        results.push({
          url: page.loc,
          status: response.status,
          valid: response.ok
        });
      } catch (error) {
        results.push({
          url: page.loc,
          status: 0,
          valid: false
        });
      }
    }
    
    return results;
  }

  // Generate blog-specific sitemap
  async generateBlogSitemap(): Promise<string> {
    const blogPosts = await this.fetchBlogPosts();
    return this.generateSitemapXml(blogPosts);
  }

  // Get sitemap statistics
  async getSitemapStats(): Promise<{
    totalUrls: number;
    staticPages: number;
    blogPosts: number;
    lastGenerated: string;
  }> {
    const blogPosts = await this.fetchBlogPosts();
    
    return {
      totalUrls: this.staticPages.length + blogPosts.length,
      staticPages: this.staticPages.length,
      blogPosts: blogPosts.length,
      lastGenerated: new Date().toISOString()
    };
  }
}

// Export singleton instance
export const enhancedSitemapService = new EnhancedSitemapService();